// frontend/app/components/lessonPlans/authoring/WizardPhaseCard.js
//
// One step of the Loop, collapsed to a header or opened onto its fields and
// the blocks inside it. Split out of WizardStepPhases so that file stays about
// the shape of the lesson rather than the shape of one card.

import React from "react";
import { StyleSheet, Text, TouchableOpacity, View } from "react-native";
import { MaterialCommunityIcons } from "@expo/vector-icons";

import BlockEditor from "./BlockEditor";
import BlockPalette from "./BlockPalette";
import { makeBlock, validateBlock } from "./blockSchemas";
import { ChipSelect, Field, IconButton, TextField } from "./WizardFields";
import { getSectionMeta, getStepSection } from "../lessonPlanTheme";

const WizardPhaseCard = ({
  colors,
  step,
  index,
  total,
  sectionOptions,
  concepts,
  boardTopicId,
  catalog,
  // Every block id in the lesson. Defaults empty so a caller that has not been
  // updated falls back to this step's ids — worse, but not broken.
  lessonBlockIds = [],
  expanded,
  onToggle,
  onChange,
  onRemove,
  onMove,
  t,
}) => {
  const translate = t || ((key) => key);
  // Section drives the card's identity (dot, eyebrow). The Loop's seven phases
  // still carry the mastery evidence, but they are a hidden key: picking a
  // section sets the step's phase to that section's first phase.
  const sectionKey = getStepSection(step);
  const section = getSectionMeta(sectionKey);
  const blocks = step.blocks || [];

  const set = (patch) => onChange({ ...step, ...patch });

  const problemCount = blocks.reduce(
    (total_, block) =>
      total_ + validateBlock(block, { conceptKeys: concepts.map((c) => c.concept_key) }).length,
    0
  );

  const setBlocks = (next) => set({ blocks: next });

  const addBlock = (type) => {
    // Scoped to the whole lesson: block ids must be unique across every step,
    // not merely within this one, or the lesson fails validation on save.
    const taken = lessonBlockIds.length
      ? lessonBlockIds
      : blocks.map((b) => b.id);
    const block = makeBlock(type, taken);
    if (block) setBlocks([...blocks, block]);
  };

  const moveBlock = (blockIndex, delta) => {
    const target = blockIndex + delta;
    if (target < 0 || target >= blocks.length) return;
    const next = [...blocks];
    next[blockIndex] = blocks[target];
    next[target] = blocks[blockIndex];
    setBlocks(next);
  };

  return (
    <View
      style={[
        styles.stepCard,
        { backgroundColor: colors.panelBg, borderColor: colors.border },
      ]}
    >
      <TouchableOpacity
        onPress={onToggle}
        accessibilityRole="button"
        accessibilityState={{ expanded }}
        accessibilityLabel={`${index + 1}. ${step.title || translate(section.label)}`}
        style={styles.stepHeader}
      >
        <View style={[styles.phaseDot, { backgroundColor: `${section.accent}22` }]}>
          <MaterialCommunityIcons name={section.icon} size={16} color={section.accent} />
        </View>

        <View style={{ flex: 1, minWidth: 0 }}>
          <Text style={[styles.stepEyebrow, { color: section.accent }]}>
            {index + 1} · {translate(section.label)}
          </Text>
          <Text style={[styles.stepTitle, { color: colors.textPrimary }]} numberOfLines={1}>
            {step.title || translate("Untitled step")}
          </Text>
          <Text style={[styles.stepMeta, { color: colors.textMuted }]}>
            {blocks.length} {translate(blocks.length === 1 ? "item" : "items")}
            {problemCount ? ` · ${problemCount} ${translate("to fix")}` : ""}
          </Text>
        </View>

        {problemCount ? (
          <MaterialCommunityIcons name="alert-circle" size={16} color={colors.warning} />
        ) : null}
        <IconButton
          colors={colors}
          icon="chevron-up"
          label={translate("Move step up")}
          disabled={index === 0}
          onPress={() => onMove(-1)}
        />
        <IconButton
          colors={colors}
          icon="chevron-down"
          label={translate("Move step down")}
          disabled={index === total - 1}
          onPress={() => onMove(1)}
        />
        <IconButton
          colors={colors}
          icon="delete-outline"
          label={`${translate("Remove step")} ${index + 1}`}
          danger
          onPress={onRemove}
        />
        <MaterialCommunityIcons
          name={expanded ? "chevron-up" : "chevron-down"}
          size={18}
          color={colors.textMuted}
        />
      </TouchableOpacity>

      {expanded ? (
        <View style={[styles.stepBody, { borderTopColor: colors.border }]}>
          <Field colors={colors} label={translate("Section")}>
            <ChipSelect
              colors={colors}
              options={sectionOptions.map((o) => ({ ...o, label: translate(o.label) }))}
              value={sectionKey}
              onChange={(nextSectionKey) =>
                set({ phase: getSectionMeta(nextSectionKey).phases[0] })
              }
              accessibilityLabel={translate("Section")}
            />
          </Field>

          <Field colors={colors} label={translate("Title")} required>
            <TextField
              colors={colors}
              value={step.title}
              onChange={(title) => set({ title })}
              placeholder={translate("Explore in 3D")}
            />
          </Field>

          <Field
            colors={colors}
            label={translate("What you do")}
            hint={translate("Teacher-only. Never shown to students.")}
          >
            <TextField
              colors={colors}
              value={step.teacher_guidance}
              onChange={(teacher_guidance) => set({ teacher_guidance })}
              placeholder={translate("Launch the scene and rotate it before naming anything.")}
              multiline
            />
          </Field>

          <Field colors={colors} label={translate("What students do")}>
            <TextField
              colors={colors}
              value={step.student_activity}
              onChange={(student_activity) => set({ student_activity })}
              placeholder={translate("Observe the model and name the structures.")}
              multiline
            />
          </Field>

          <Text style={[styles.blocksHeading, { color: colors.textSecondary }]}>
            {translate("Content in this step")}
          </Text>

          {blocks.map((block, blockIndex) => (
            <BlockEditor
              key={block.id}
              colors={colors}
              block={block}
              concepts={concepts}
              boardTopicId={boardTopicId}
              catalog={catalog}
              onChange={(next) =>
                setBlocks(blocks.map((b, i) => (i === blockIndex ? next : b)))
              }
              onRemove={() => setBlocks(blocks.filter((_, i) => i !== blockIndex))}
              onMove={(delta) => moveBlock(blockIndex, delta)}
              isFirst={blockIndex === 0}
              isLast={blockIndex === blocks.length - 1}
              t={t}
            />
          ))}

          <BlockPalette colors={colors} stepPhase={step.phase} onAdd={addBlock} t={t} />
        </View>
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  stepCard: { borderRadius: 14, borderWidth: 1, marginBottom: 10, overflow: "hidden" },
  stepHeader: {
    flexDirection: "row",
    alignItems: "center",
    padding: 12,
    gap: 8,
  },
  phaseDot: {
    width: 34,
    height: 34,
    borderRadius: 10,
    alignItems: "center",
    justifyContent: "center",
  },
  stepEyebrow: {
    fontSize: 10,
    fontWeight: "800",
    letterSpacing: 0.5,
    textTransform: "uppercase",
  },
  stepTitle: { fontSize: 14, fontWeight: "700", marginTop: 2 },
  stepMeta: { fontSize: 11, marginTop: 2 },
  stepBody: { borderTopWidth: 1, padding: 14 },

  blocksHeading: {
    fontSize: 11,
    fontWeight: "800",
    letterSpacing: 0.5,
    textTransform: "uppercase",
    marginBottom: 10,
    marginTop: 4,
  },
});

export default WizardPhaseCard;
